import React from 'react';
import {
  type UseDismissProps,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useFloatingTree as useFloatingTreeBase,
} from '@floating-ui/react';

import {emptyObject} from '@packages/utilities/fp/utils';

export type FloatingEvents = {
  legacyOverlay: {
    id: string;
    parentId: string;
    isOpen: boolean;
  };
};

type Listener<Event extends keyof FloatingEvents> = (
  data: FloatingEvents[Event]
) => void;

/**
 * Adds types to the `useFloatingTree` hook.
 */
export const useFloatingTree = () => {
  const tree = useFloatingTreeBase();

  const emit = React.useCallback(
    <Event extends keyof FloatingEvents>(
      event: Event,
      data: FloatingEvents[Event]
    ) => tree?.events.emit(event, data),
    [tree?.events]
  );

  const on = React.useCallback(
    <Event extends keyof FloatingEvents>(
      event: Event,
      listener: Listener<Event>
    ) => tree?.events.on(event, listener),
    [tree?.events]
  );

  const off = React.useCallback(
    <Event extends keyof FloatingEvents>(
      event: Event,
      listener: Listener<Event>
    ) => tree?.events.off(event, listener),
    [tree?.events]
  );

  return React.useMemo(
    () => ({
      ...tree,
      events: {
        ...tree?.events,
        emit,
        on,
        off,
      },
    }),
    [tree, emit, on, off]
  );
};

export const useRegisterLegacyOverlay = ({isOpen}: {isOpen?: boolean}) => {
  const nodeId = useFloatingNodeId();
  const parentId = useFloatingParentNodeId();
  const tree = useFloatingTree();
  React.useEffect(() => {
    if (!parentId || !nodeId) return;

    if (isOpen) {
      tree?.events.emit('legacyOverlay', {
        id: nodeId,
        parentId,
        isOpen: true,
      });
    } else {
      tree?.events.emit('legacyOverlay', {
        id: nodeId,
        parentId,
        isOpen: false,
      });
    }

    return () => {
      // if we unmount the Anchor without changing `open`, we still want to de-register the node from the parent Modal.
      tree?.events.emit('legacyOverlay', {
        id: nodeId,
        parentId,
        isOpen: false,
      });
    };
  }, [parentId, nodeId, isOpen, tree]);

  return React.useMemo(() => ({nodeId}), [nodeId]);
};

const SELECTORS = {
  floatingUI: '[data-floating-ui-portal]',
  toastify: '.Toastify',
  designerNotifications: '.bem-NotificationList',
};

export const useLegacyOverlayCoordinator = ({
  onOutsidePress: onOutsidePressProp,
}: {
  onOutsidePress?: UseDismissProps['outsidePress'];
} = emptyObject) => {
  const [openLegacyOverlays, setOpenLegacyOverlays] = React.useState<
    Array<string>
  >([]);
  const nodeId = useFloatingNodeId();
  const tree = useFloatingTree();

  /**
   *
   * The legacy `Popover` components use the KeyboardContext HOC to add its event listeners to `body.document`.
   * This event then bubbles up through the `Modal` event listeners before reaching the document.
   * The `Modal` captures it and takes over its escape handling.
   *
   * So, what we do is register any `Popover` that opens within the `Modal`.
   * If there are any registered, we prevent the `Modal` from using its escape key.
   *
   * In addition, we also need to register other `Overlay` components, such as the React-Select powered `Autocomplete`.
   * This way we can ensure that the `Modal` does not close on outside click when these components are open.
   *
   * Once we fully remove legacy Popover, we can rip this logic out.
   */
  React.useEffect(() => {
    const handlePopovers = ({
      id,
      parentId,
      isOpen,
    }: FloatingEvents['legacyOverlay']) => {
      if (parentId === nodeId || openLegacyOverlays.includes(parentId)) {
        if (isOpen) {
          setOpenLegacyOverlays((current) => [...current, id]);
        } else {
          setOpenLegacyOverlays((current) =>
            current.filter((popoverId) => popoverId !== id)
          );
        }
      }
    };

    tree?.events.on('legacyOverlay', handlePopovers);
    return () => tree?.events.off('legacyOverlay', handlePopovers);
  }, [openLegacyOverlays, setOpenLegacyOverlays, tree, nodeId]);

  const onOutsidePress = React.useCallback(
    // This is a MouseEvent, but Typescript is screaming that target.closest doesn't exist on it.
    (event: any) => {
      if (onOutsidePressProp && typeof onOutsidePressProp === 'function') {
        onOutsidePressProp(event);
      }

      return !(
        event.target.closest(SELECTORS.toastify) ||
        event.target.closest(SELECTORS.designerNotifications) ||
        openLegacyOverlays.length > 0
      );
    },
    [openLegacyOverlays, onOutsidePressProp]
  );

  return React.useMemo(
    () => ({nodeId, onOutsidePress, openLegacyOverlays}),
    [nodeId, onOutsidePress, openLegacyOverlays]
  );
};
